(ns app.front.view.workspace.primary-actions
  "Handle automatic primary / secondary actions in workspace view

  Here, you can trigger special re-frame events instead of submitting the signal as-is. To
  implement custom parameter modals, see `app.front.view.application.action-parameters/parameter-view`."
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.form.subs :as form-subs]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.admin.permissions :as admin-permissions]
            [app.front.view.application.action-parameters :as action-parameters]
            [app.front.view.application.delete :as delete-app]
            [app.front.view.application.utils :as app-utils]
            [app.front.view.application.work-type-target :as work-select]
            [app.front.view.audit-log.list :as-alias audit-log-list]
            [app.front.view.occupancy.edit :as partial-occupancy]
            [app.front.view.periodic-inspection.delete :as delete-pi]
            [app.front.view.pre-consultation.create-application :as pre-con-create-app]
            [app.front.view.pre-consultation.create-linked-workspaces :as pre-con-create-linked-wss]
            [app.front.view.property.issues :as property-issues]
            [app.front.view.property.owner :as property-owner]
            [app.front.view.sign-off :as sign-off]
            [app.front.view.workspace.add-related :as add-related]
            [app.front.view.workspace.archival-on-finished :as archival-on-finished]
            [app.front.view.workspace.copy :as-alias workspace-copy]
            [app.front.view.workspace.create-related :as create-related]
            [app.front.view.workspace.header :as ws-header]
            [app.front.view.workspace.neighbour-properties :as neighbour-properties]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.set :as set]
            [core.feature-flag.model :as ff-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;;
;; Signal-specifics
;;


(def primary-button-tr
  "For modal, the tr-key of the text to be shown on the modal apply button (default :yes)"
  {:process.signal/delete                  :delete.application/delete
   :process.signal/apply-partial-occupancy :occupancy/sign-off-and-apply
   :process.signal/link-existing-workspace :related-workspace/link-and-close
   :process.signal/revert-to-submitted     :application/revert-to-submitted
   :process.signal/back-to-submitted       :process.signal/back-to-submitted})

(def secondary-button-tr
  "For modal, the tr-key of the text to be shown on the modal cancel button (default :no)"
  {:process.signal/delete                  :delete.application/cancel
   :process.signal/apply-partial-occupancy :occupancy/cancel-application
   :process.signal/link-existing-workspace :cancel
   :process.signal/revert-to-submitted     :cancel
   :process.signal/back-to-submitted       :cancel})

(def title-tr
  {:process.signal/delete                      :delete.application/heading
   :process.signal/ready-to-issue              :ready-to-issue/confirm-modal-title
   :process.signal/revert-to-submitted         :application/revert-to-submitted
   :process.signal/back-to-submitted           :process.signal/back-to-submitted
   :process.signal/delete-inspection-workspace :delete.inspection.workspace/heading})

(def confirm-modal-text-tr
  {:process.signal/delete                      :delete.application/content
   :process.signal/cancel-sign-off             :cancel-sign-off/confirm-modal-text
   :process.signal/cancel-all-sign-offs        :cancel-sign-off/confirm-modal-text
   :process.signal/apply-partial-occupancy     :intentionally-blank-string
   :process.signal/restore-verdict-to-draft    :restore-verdict-to-draft/confirm-modal-text
   :process.signal/deem-as-complete            :application/confirm-complete
   :process.signal/set-property-verified       :application.property/confirm-set-property-verified
   :process.signal/link-existing-workspace     :related-workspace/add-modal-help
   :process.signal/delete-application-only     :application/delete-draft-help
   :process.signal/set-finished                :work/set-finished-help
   :process.signal/revert-from-approved        :revert-from-approved/confirm-modal-text
   :process.signal/delete-inspection-workspace :delete.inspection-workspace/content})

(def signal-icons
  {:process.signal/sync-to-back-end-system       i/archives-in
   :process.signal/send-bills-to-backend-system  i/archives-in
   :process.signal/sync-permit-to-ecm-system     i/archives-in
   :process.signal/sync-ws-package-to-ecm-system i/archives-in
   :process.signal/delete                        i/trash
   :process.signal/sign-off-view                 i/undersign
   :process.signal/revoke                        i/undo
   :process.signal/revoke-licence                i/undo
   :process.signal/link-existing-workspace       i/link
   :process.signal/create-supplemental-workspace i/plus
   :process.signal/verify-bond                   i/check})

(def signal-handlers
  {:process.signal/delete                                             ::delete-app/delete-application
   :process.signal/delete-application-only                            ::delete-app/delete-application-draft
   :process.signal/sign-off-view                                      ::sign-off/show-sign-off-view
   :process.signal/sign-off-view-visit                                ::sign-off/show-sign-off-view
   :process.signal/apply-partial-occupancy                            ::partial-occupancy/apply-partial-occupancy
   :process.signal/check-property-owners                              ::property-owner/get-property-owner-info
   :process.signal/add-additional-property                            ::ws-header/set-add-properties-visible
   :process.signal/change-primary-property                            ::ws-header/set-change-property-visible
   :process.signal/edit-workspace-location                            ::ws-header/set-edit-workspace-location-visible
   :process.signal/edit-neighbour-properties                          ::neighbour-properties/add-neighbour-property-view
   :process.signal/check-property-issues                              ::property-issues/init
   :process.signal/plans-resubmission                                 ::create-related/plans-resubmission
   :process.signal/begin-transfer-of-permit                           ::create-related/begin-transfer-of-permit
   :process.signal/phased-plan-approval                               ::create-related/phased-plan-approval
   :process.signal/create-supplemental-workspace                      ::create-related/create-licence-follow-up
   :process.signal/copy-workspace                                     ::workspace-copy/show-copy-view
   :process.signal/create-linked-b-workspace                          ::workspace-copy/show-create-linked-b-ws-view
   :process.signal/add-admin-write-authorization                      ::admin-permissions/add-admin-write-authorization
   :process.signal/remove-admin-write-authorization                   ::admin-permissions/remove-admin-write-authorization
   :process.signal/renew-licence                                      ::create-related/renew-licence
   :process.signal/renew-registration                                 ::create-related/renew-contractor-registration
   :process.signal/view-audit-log                                     ::audit-log-list/show-audit-log-view
   :process.signal/edit-ws-config-path                                ::work-select/enter-edit-mode
   :process.signal/edit-ws-config-path-in-pre-con                     ::work-select/enter-edit-mode
   :process.signal/create-application-from-pre-consultation           ::pre-con-create-app/create-from-pre-con
   :process.signal/create-application-from-completed-pre-consultation ::pre-con-create-app/create-from-pre-con
   :process.signal/start-archiving-process                            ::pre-con-create-app/create-archiving-phase
   :process.signal/create-linked-workspaces-from-pre-consultation     ::pre-con-create-linked-wss/init
   :process.signal/delete-inspection-workspace                        ::delete-pi/delete-inspection-workspace})

(def signal-events
  {:process.signal/set-finished ::archival-on-finished/get-default-metadata})

(def signal-modal-class
  {:process.signal/apply-partial-occupancy "apply-partial-occupancy"})

(def init-confirm
  "For signal in k, dispatch v when confirm-dialog is shown"
  {:process.signal/link-existing-workspace ::add-related/fetch-workspaces-for-property})

(def sync-signal?
  #{:process.signal/sync-to-back-end-system
    :process.signal/send-bills-to-backend-system
    :process.signal/sync-permit-to-ecm-system
    :process.signal/sync-ws-package-to-ecm-system})

(def backend-system-signal?
  "Signals that require the backend system name (e.g., 'Amanda') as a translation parameter"
  #{:process.signal/sync-to-back-end-system
    :process.signal/send-bills-to-backend-system})

(def ecm-system-signal?
  "Signals that require the ECM system name as a translation parameter"
  #{:process.signal/sync-permit-to-ecm-system
    :process.signal/sync-ws-package-to-ecm-system})

;;
;; Confirmation handling
;;

(rf/reg-event-fx ::confirm-init
  (fn [{:keys [db]} [_ success-event {:keys [signal process-target] :as options}]]
    (p/assoc-if {:db (assoc db ::confirm {:event   success-event
                                          :options (or options {})})}
                :dispatch
                (some-> (get signal-events signal)
                        (vector process-target)))))

(rf/reg-event-db ::set-confirm-signal
  ;; Note: Does not override which "primary action" signal the confirm is handling,
  ;; just the event that will be dispatched to the backend
  (fn [db [_ signal]]
    (update-in db [::confirm :event]
               (fn [[evt target _]]
                 [evt target signal]))))

(rf/reg-event-db ::set-confirm-signal-parameters
  (fn [db [_ parameters]]
    (update-in db
               [::confirm :event]
               (fn [[evt target [signal-name prev-params]]]
                 [evt
                  target
                  [signal-name (merge prev-params parameters)]]))))

(rf/reg-sub ::confirm-data
  :-> ::confirm)

(rf/reg-sub ::confirm-signal
  :<- [::confirm-data]
  (fn [data _]
    (let [event (:event data)]
      ;; when special-handler is applied the signal is the event itself
      (if (coll? (last event))
        (last event)
        event))))

(rf/reg-sub ::confirm-signal-parameters
  :<- [::confirm-signal]
  (fn [[_signal & [params]] _]
    params))

(rf/reg-event-fx ::confirm-ok
  (fn [{db :db} _]
    {:dispatch (get-in db [::confirm :event])
     :db       (dissoc db ::confirm)}))


(rf/reg-event-db ::confirm-cancel
  (fn [db _]
    (dissoc db ::confirm)))


(rf/reg-sub ::confirm-options
  :<- [::confirm-data]
  :-> :options)

(rf/reg-sub ::process-target
  :<- [::confirm-data]
  (fn [data _]
    (-> data :event second)))


(defn admin-special-actions [admin? can-write?]
  (when admin?
    (if can-write?
      [{:signal-id :process.signal/remove-admin-write-authorization
        :ui-type   :select}]
      [{:signal-id :process.signal/add-admin-write-authorization
        :ui-type   :select
        :confirm?  true}])))


(rf/reg-sub ::admin-special-actions
  :<- [:session/admin?]
  (fn [admin? [_ can-write?]]
    (admin-special-actions admin? can-write?)))


(defn- signal-rf-event [signal-id process-target]
  (if-let [special-handler (signal-handlers signal-id)]
    [special-handler process-target [signal-id {}]]
    [::process/signal process-target [signal-id {}]]))

(defn handle-action-confirm-init [signal-id process-target]
  (let [event (signal-rf-event signal-id process-target)]
    (>evt [::confirm-init event {:signal         signal-id
                                 :process-target process-target}])
    (when-let [also-dispatch (init-confirm signal-id)]
      (>evt [also-dispatch process-target]))))

(rf/reg-event-fx ::trigger
  (fn [_ [_ {:keys [signal-id confirm? process-target]}]]
    (if confirm?
      (do
        (handle-action-confirm-init signal-id process-target)
        {})
      {:dispatch (signal-rf-event signal-id process-target)})))

;;
;; Helpers and components
;;

(defn handle-action [{:keys [signal-id confirm? process-target]}]
  (if confirm?
    (handle-action-confirm-init signal-id process-target)
    (>evt (signal-rf-event signal-id process-target))))

(defn- select-item->handle-action-fn [process-target]
  #(handle-action
     (util/mmap {:signal-id   (apply keyword (futil/oget % :selectedItem :value))
                 :signal-type (some-> (futil/oget % :selectedItem :signalType)
                                      keyword)
                 :confirm?    (futil/oget % :selectedItem :confirm)}
                process-target)))

(defn loading-tr-key [signal-id]
  [(ui/append-to-keyword-ns ".loading" signal-id) :processing])

(defn back-end-sync-loading-desc [tr]
  (str (tr (loading-tr-key :process.signal/sync-to-back-end-system)
           [(<sub [::ws-subs/backend-system-name])])
       "..."))

(defn ecm-sync-loading-desc [tr signal-id]
  (str (tr (loading-tr-key signal-id)
           [(<sub [::ws-subs/ecm-system-name])])
       "..."))

(defmulti loading-desc (fn [signal _tr] signal))

(defmethod loading-desc :process.signal/sync-to-back-end-system [_signal-id tr]
  (back-end-sync-loading-desc tr))

(defmethod loading-desc :process.signal/send-bills-to-backend-system [_signal-id tr]
  (back-end-sync-loading-desc tr))

(defmethod loading-desc :process.signal/sync-ws-package-to-ecm-system [signal-id tr]
  (ecm-sync-loading-desc tr signal-id))

(defmethod loading-desc :process.signal/sync-permit-to-ecm-system [signal-id tr]
  (ecm-sync-loading-desc tr signal-id))

(defmethod loading-desc :default
  [signal-id tr]
  (str (tr (loading-tr-key signal-id))
       "..."))

(defn generic-sync-button [{:keys [tr workspace signal-id button-opts processing?
                                   system-name-sub state-attr in-progress-value]}]
  (let [backend-system (<sub [system-name-sub])]
    (if (or processing? (-> workspace state-attr (= in-progress-value)))
      (cc/inline-loading {:class-name  "w-56"
                          :description (loading-desc signal-id tr)})
      (cc/button button-opts (tr [signal-id]
                                 [backend-system])))))

(defn ecm-sync-button [ctx]
  (generic-sync-button (merge ctx
                              {:system-name-sub   ::ws-subs/ecm-system-name
                               :state-attr        :workspace/ecm-sync-state
                               :in-progress-value :workspace.ecm-sync-state/in-progress})))

(defn back-end-sync-button [ctx]
  (generic-sync-button (merge ctx
                              {:system-name-sub   ::ws-subs/backend-system-name
                               :state-attr        :workspace/integration-sync-state
                               :in-progress-value :in-progress})))

(defmulti sync-action-button
  "Backend sync signal has some special loading spinner requirements"
  :signal-id)

(defmethod sync-action-button :process.signal/sync-to-back-end-system [ctx]
  (back-end-sync-button ctx))

(defmethod sync-action-button :process.signal/send-bills-to-backend-system [ctx]
  (back-end-sync-button ctx))

(defmethod sync-action-button :process.signal/sync-permit-to-ecm-system [ctx]
  (ecm-sync-button ctx))

(defmethod sync-action-button :process.signal/sync-ws-package-to-ecm-system [ctx]
  (ecm-sync-button ctx))

(defn signal-label
  ([signal-id tr]
   (signal-label signal-id tr nil))
  ([signal-id tr {:keys [backend-system-name ecm-system-name]}]
   (let [system-name (cond
                       (backend-system-signal? signal-id) backend-system-name
                       (ecm-system-signal? signal-id)     ecm-system-name)]
     (tr [signal-id] [system-name]))))

(defn process-action-button [tr signal-id button-opts processing?]
  (if processing?
    (cc/inline-loading {:description (str (tr (loading-tr-key signal-id)) "...")})
    (cc/button
      (assoc button-opts :icon-position :right)
      (signal-label signal-id tr))))

(defn- buttons->dropdown-items [tr select]
  (->> select
       (map (fn [{:keys [signal-id signal-type confirm? status]}]
              {:label      (signal-label signal-id tr)
               :signalType signal-type
               :confirm    confirm?
               :value      [(namespace signal-id) (name signal-id)]
               :disabled   (= status :disabled)}))
       (sort-by :label)))


(defn multiple? [items]
  (->> items count (< 1)))

(defn is-primary? [{:keys [ui-type]}]
  (= ui-type :primary))

(defn is-disabled? [{:keys [status]}]
  (= status :disabled))

(defn primary-actions-buttons-expanded [{:keys [buttons processing-signals process-target tr workspace]}]
  (let [hide-disabled? (->> buttons (filter is-primary?) multiple?)]
    (for-frag [{:keys [signal-id ui-type status errors]
                :as   button} buttons
               :let [disabled?   (= status :disabled)
                     processing? (contains? processing-signals signal-id)
                     button-opts (p/assoc-if
                                   {:key              signal-id
                                    ;; current style uses Carbon "tertiary" as secondary.
                                    :kind             (if (= ui-type :secondary) :tertiary ui-type)
                                    :size             :small
                                    :class-name       "process-action"
                                    :data-cy          (str "process-action-" (name signal-id))
                                    :icon             (get signal-icons signal-id i/arrow-right)
                                    :icon-description ""
                                    :on-click         #(handle-action (-> button
                                                                          (select-keys [:signal-id
                                                                                        :signal-type
                                                                                        :confirm?])
                                                                          (assoc :process-target process-target)))
                                    :disabled         disabled?}
                                   :aria-describedby (when errors
                                                       "errors-guard-results"))]]
      (when (or (not disabled?) (not hide-disabled?))
        [:div {:key signal-id}
         (if (sync-signal? signal-id)
           (sync-action-button (util/mmap tr
                                          workspace
                                          signal-id
                                          button-opts
                                          processing?))
           (process-action-button tr signal-id button-opts processing?))]))))

(defn primary-actions-buttons-dropdown [{:keys [buttons processing-signals process-target tr]}]
  (let [common-label (or (some->> buttons
                                  (map :signal-id)
                                  (map #(signal-label % tr))
                                  str-util/common-prefix)
                         (tr [:primary-actions/proceed]))]
    [:div
     (if-let [[first-processing-primary-signal & _] (->> buttons
                                                         (map :signal-id)
                                                         set
                                                         (set/intersection processing-signals)
                                                         seq)]
       (cc/inline-loading {:description (str (tr (loading-tr-key first-processing-primary-signal)) "...")})
       (cc/dropdown
         {:id            "primary-actions-select"
          :label         ""
          :title-text    common-label
          :hide-label    true
          :class-name    "primary-select select-wide-menu"
          :items         (buttons->dropdown-items tr buttons)
          :selected-item {:label common-label}
          :on-change     (select-item->handle-action-fn process-target)}))]))

(defn ^{:arglists '([{:keys [buttons processing-signals process-target tr workspace] :as ctx}])}
  primary-actions-buttons [{:keys                                                          [buttons] :as ctx}]
  (let [primary-buttons (->> buttons
                             (filter is-primary?))]
    (cond
      (and (or
             ;; show menu, if multiple available primary actions
             (->> primary-buttons
                  (remove is-disabled?)
                  multiple?)
             ;; also show menu if multiple disabled primary actions and no enabled ones
             (and
               (->> primary-buttons
                    (filter is-disabled?)
                    multiple?)
               (->> primary-buttons
                    (remove is-disabled?)
                    empty?)))
           ;; sync-to-back-end-system is different, it gets a deliberately different separate button,
           ;; so when that is present, just render as normal buttons
           (not (contains? (->> buttons (map :signal-id) set)
                           :process.signal/sync-to-back-end-system)))
      [primary-actions-buttons-dropdown ctx]
      :else
      [primary-actions-buttons-expanded ctx])))


(defn- ready-to-issue-confirm-text [{:keys [tr]}]
  (if (seq (<sub [::form-subs/waiting-for-customer-forms (<sub [::ws-subs/application])]))
    {:dangerouslySetInnerHTML (r/unsafe-html (tr [:ready-to-issue/confirm-modal-text-remove-tasks]))}
    (tr [:ready-to-issue/confirm-modal-text])))


(defn- renew-licence-confirm-text [{:keys [tr]}]
  (let [process-target (<sub [::process-target])
        ctx            (<sub [::entities/context])
        licence        (entities/find-entity ctx (if (number? process-target) process-target (:db/id process-target)))
        active?        (some-> licence :licence/category-group-config du/is-active?)]
    (if active?
      (tr [:are-you-sure])
      (let [lang      (<sub [:language])
            muni-name (municipality-model/municipality-name (:licence/municipality licence) lang)]
        (tr [:licence.renew/category-unavailable] [muni-name])))))

(defn- renew-licence-category-inactive? []
  (let [process-target (<sub [::process-target])
        ctx            (<sub [::entities/context])
        licence        (entities/find-entity ctx (if (number? process-target) process-target (:db/id process-target)))]
    (not (some-> licence :licence/category-group-config du/is-active?))))


(defn- revert-to-submitted-confirm-text [{:keys [tr]}]
  [ui/bubble-content
   (tr [(if (= :permit-type/P (<sub [::ws-subs/permit-type]))
          :application/revert-to-submitted-confirm-modal-text-planning
          :application/revert-to-submitted-confirm-modal-text)])])

(defn- confirmation-modal-text [{:keys [tr signal] :as opts}]
  (case signal
    :process.signal/ready-to-issue (ready-to-issue-confirm-text opts)
    :process.signal/renew-licence (renew-licence-confirm-text opts)
    :process.signal/revert-to-submitted (revert-to-submitted-confirm-text opts)
    (if (contains? confirm-modal-text-tr signal)
      ;; tr-key specified, can be nil which causes no text
      (when-let [tr-key (get confirm-modal-text-tr signal)]
        [ui/bubble-content (tr [tr-key])])
      ;; no tr-key, go with default
      (tr [:are-you-sure]))))


(defn confirmation-modal []
  (let [tr              (<sub [::ws-subs/tr-quiet])
        {:keys [signal]
         :as   confirm} (<sub [::confirm-options])
        cancel-fn       #(>evt [::confirm-cancel])]
    (when signal
      (cc/modal {:open                    (boolean confirm)
                 :primary-button-text     (tr [(primary-button-tr signal :yes)])
                 :secondary-button-text   (tr [(secondary-button-tr signal :no)])
                 :modal-heading           (or (tr [(title-tr signal signal)])
                                              (tr [:are-you-sure]))
                 :on-request-close        cancel-fn
                 :on-request-submit       #(>evt [::confirm-ok])
                 :on-secondary-submit     cancel-fn
                 :primary-button-disabled (or (->> (<sub [::confirm-signal-parameters])
                                                   (action-parameters/valid? signal)
                                                   not)
                                              (and (= signal :process.signal/renew-licence)
                                                   (renew-licence-category-inactive?)))
                 :class-name              (str "primary-action-modal " (get signal-modal-class signal))}
                [:div.flex.flex-col
                 {:data-cy (if (keyword? signal)
                             (name signal)
                             signal)}
                 [:div.bx--modal-content__text.mb-6
                  (when-let [content (confirmation-modal-text (util/mmap tr signal))]
                    [:div.mb-6 content])
                  [action-parameters/parameter-view
                   (util/mmap {:on-change      #(>evt [::set-confirm-signal-parameters %])
                               :parameters     (<sub [::confirm-signal-parameters])
                               :process-target (<sub [::process-target])}
                              signal
                              tr)]]]))))


(defn primary-actions-for-sub-header [match]
  (let [loading?           (or (<sub [::ws-subs/loading?])
                               (<sub [::ws-subs/loading-config?])
                               (<sub [:comm/command-processing? :group/process-states]))
        tr                 (<sub [::ws-subs/tr])
        phase              (-> match :data :phase)
        workspace          (<sub [::ws-subs/workspace])
        process-sub        (process/workspace-process-subs phase)
        process            (when process-sub (<sub [process-sub]))
        process-target-sub (process/workspace-process-target-subs phase)
        process-target     (when process-target-sub (<sub [process-target-sub]))
        processing-signals (<sub [::process/current-processing-signals])
        can-write?         (-> workspace :granted-permissions :permission.application/review)
        admin-actions      (when workspace
                             ;; TODO do we need admin actions for licences?
                             (<sub [::admin-special-actions can-write?]))
        actions            (concat (app-utils/resolve-primary-actions process true)
                                   admin-actions)
        buttons            (->> actions (filter #(= (:ui-type %) :primary)))]
    (when phase
      (if loading?
        [:div
         (cc/button-skeleton)]
        [primary-actions-buttons (util/mmap buttons processing-signals process-target tr workspace)]))))


(defn- sync-signal-loading?
  "Check if a sync signal is currently loading based on workspace state and processing signals."
  [signal-id workspace processing-signals]
  (or (contains? processing-signals signal-id)
      (and (backend-system-signal? signal-id)
           (= :in-progress (:workspace/integration-sync-state workspace)))
      (and (ecm-system-signal? signal-id)
           (= :workspace.ecm-sync-state/in-progress (:workspace/ecm-sync-state workspace)))))

(defn signal->action
  "Convert a signal map to an action map for the options panel.
   Optional context can include:
   - :backend-system-name and :ecm-system-name for signal label translation
   - :workspace and :processing-signals for loading state detection"
  ([tr process-target signal-map]
   (signal->action tr process-target signal-map nil))
  ([tr process-target {:keys [signal-id signal-type confirm? status]}
    {:keys [workspace processing-signals] :as context}]
   (let [label-context (select-keys context [:backend-system-name :ecm-system-name])
         loading?      (and (sync-signal? signal-id)
                            (sync-signal-loading? signal-id workspace processing-signals))]
     (cond-> {:id       signal-id
              :label    (signal-label signal-id tr label-context)
              :disabled (= status :disabled)
              :on-click #(>evt [:app.front.view.workspace.primary-actions/trigger
                                {:signal-id      signal-id
                                 :signal-type    signal-type
                                 :confirm?       confirm?
                                 :process-target process-target}])}
       loading? (assoc :loading?            true
                       :loading-description (loading-desc signal-id tr))))))

(defn secondary-actions [{:keys [process]}]
  (let [disabled-signals (into #{} (mapcat (fn [[flag signals]]
                                             (when (ff/enabled? flag) signals)))
                               ff-model/flag->hidden-signals)
        resolved         (-> (app-utils/resolve-primary-actions process true)
                             (cond->>
                               (seq disabled-signals)
                               (remove #(contains? disabled-signals (:signal-id %)))))]
    (or (remove is-primary? resolved) [])))
